import {
  offListProblem,
  offListRegion,
  type Place,
  regionList,
  strayRegion,
  withCountry,
} from '../lib/address-place'
import { useCountryOptions } from '../lib/country-options'
import { Field, FieldRow, Notice } from './index'
import { Select } from './Select'

/**
 * Where an address is: its country, and — for a US or Canadian one — its state
 * or province, picked from that country's list and stored as its code. Every
 * other country names no region, so the field is not there at all.
 *
 * One row of two fields, which folds to one column on a narrow measure the way
 * every `FieldRow` does.
 *
 * A region held that the list does not name is shown as the current value
 * with what to do beneath it rather than blanked: a form that silently dropped
 * it would lose the only record of it. Picking from the list replaces it; the
 * save path (`placeToSend`) never sends it back.
 *
 * A state held on an address whose country names none opens already cleared
 * (`openPlace`), and `held` — the place as stored — is what lets the row say
 * so, as a note rather than a fault: saving is the fix.
 */
export function AddressPlace({
  value,
  onValueChange,
  regionOptional = false,
  held,
}: {
  value: Place
  onValueChange: (place: Place) => void
  /** The place as stored, where a form is editing one. */
  held?: Place
  /** Marks the state or province as one the form takes left blank. */
  regionOptional?: boolean
}) {
  const countries = useCountryOptions()
  const list = regionList(value.country)
  const offList = offListRegion(value)
  const problem = offListProblem(value)
  const stray = held ? strayRegion(held) : null
  const clearing =
    stray !== null && value.country === held?.country && !value.region

  return (
    <>
      <FieldRow>
        <Field label="Country" problem={list ? null : problem}>
          <Select
            value={value.country || null}
            onValueChange={(country) =>
              onValueChange(withCountry(value, country))
            }
            options={countries}
            placeholder="Choose a country"
            searchable
          />
        </Field>
        {list ? (
          <Field label={list.label} optional={regionOptional} problem={problem}>
            <Select
              value={offList === null ? value.region || null : null}
              onValueChange={(region) => onValueChange({ ...value, region })}
              options={list.options}
              placeholder={offList ?? list.placeholder}
              searchable
            />
          </Field>
        ) : null}
      </FieldRow>
      {clearing ? (
        <Notice tone="note">
          “{stray}” is held as a state, which an address outside the US and
          Canada does not name. Saving clears it.
        </Notice>
      ) : null}
    </>
  )
}
